import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    BarChart3, Calendar, ChevronLeft, ChevronRight,
    Package, TrendingUp
} from 'lucide-react';

function SellerSettlement({ userInfo }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

    const loadOrders = useCallback(async () => {
        try {
            setLoading(true);
            const sellerId = userInfo.uid;

            const monthStart = new Date(selectedYear, selectedMonth - 1, 1);
            const monthEnd = new Date(selectedYear, selectedMonth, 1);

            const ordersRef = collection(db, 'marketplaceOrders');
            const q = query(
                ordersRef,
                where('sellerId', '==', sellerId),
                where('status', 'in', ['delivered', 'paid', 'confirmed', 'shipped']),
                where('createdAt', '>=', monthStart),
                where('createdAt', '<', monthEnd)
            );

            const snapshot = await getDocs(q);
            const list = snapshot.docs.map(doc => {
                const data = doc.data();
                const items = data.items || [];
                const productName = items.length > 0
                    ? items[0].productName + (items.length > 1 ? ` 외 ${items.length - 1}건` : '')
                    : '상품';
                const total = data.payment?.total || data.totalAmount ||
                    items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0);

                return {
                    id: doc.id,
                    buyerName: data.buyerName || '구매자',
                    productName,
                    total,
                    status: data.status,
                    createdAt: data.createdAt,
                };
            });

            list.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                return dateB - dateA;
            });

            setOrders(list);
        } catch (error) {
            console.error('매출 데이터 로드 실패:', error);
        } finally {
            setLoading(false);
        }
    }, [userInfo, selectedYear, selectedMonth]);

    useEffect(() => {
        if (userInfo) loadOrders();
    }, [userInfo, loadOrders]);

    const handlePrevMonth = () => {
        if (selectedMonth === 1) {
            setSelectedYear(y => y - 1);
            setSelectedMonth(12);
        } else {
            setSelectedMonth(m => m - 1);
        }
    };

    const handleNextMonth = () => {
        const now = new Date();
        if (selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1) return;
        if (selectedMonth === 12) {
            setSelectedYear(y => y + 1);
            setSelectedMonth(1);
        } else {
            setSelectedMonth(m => m + 1);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return `${date.getMonth() + 1}/${date.getDate()}`;
    };

    const statusLabel = (status) => {
        const map = {
            paid: '결제완료', confirmed: '주문확인',
            shipped: '배송중', delivered: '수령완료'
        };
        return map[status] || status;
    };

    const deliveredOrders = orders.filter(o => o.status === 'delivered');
    const totalSales = deliveredOrders.reduce((sum, o) => sum + o.total, 0);
    const isCurrentMonth = selectedYear === new Date().getFullYear() && selectedMonth === new Date().getMonth() + 1;

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <BarChart3 size={28} color="#6366f1" />
                <h1 style={styles.title}>매출 현황</h1>
            </div>

            {/* 월 선택 */}
            <div style={styles.monthSelector}>
                <button onClick={handlePrevMonth} style={styles.monthBtn}>
                    <ChevronLeft size={20} />
                </button>
                <span style={styles.monthText}>
                    <Calendar size={18} color="#6366f1" />
                    {selectedYear}년 {selectedMonth}월
                </span>
                <button
                    onClick={handleNextMonth}
                    style={{ ...styles.monthBtn, ...(isCurrentMonth ? { opacity: 0.3, cursor: 'default' } : {}) }}
                    disabled={isCurrentMonth}
                >
                    <ChevronRight size={20} />
                </button>
            </div>

            {/* 요약 */}
            <div style={styles.summary}>
                <div style={styles.summaryItem}>
                    <Package size={20} color="#6366f1" />
                    <div>
                        <div style={styles.summaryLabel}>완료된 주문</div>
                        <div style={styles.summaryValue}>{deliveredOrders.length}건</div>
                    </div>
                </div>
                <div style={styles.divider} />
                <div style={styles.summaryItem}>
                    <TrendingUp size={20} color="#10b981" />
                    <div>
                        <div style={styles.summaryLabel}>총 매출</div>
                        <div style={{ ...styles.summaryValue, color: '#10b981' }}>
                            {totalSales.toLocaleString()}원
                        </div>
                    </div>
                </div>
            </div>

            {/* 주문 목록 */}
            <div style={styles.listCard}>
                {loading ? (
                    <div style={styles.empty}>
                        <p style={styles.emptyText}>불러오는 중...</p>
                    </div>
                ) : orders.length === 0 ? (
                    <div style={styles.empty}>
                        <Package size={48} color="#cbd5e1" />
                        <p style={styles.emptyText}>이 달의 주문이 없습니다</p>
                    </div>
                ) : (
                    <>
                        {/* 테이블 헤더 */}
                        <div style={styles.tableHeader}>
                            <span style={{ ...styles.col, flex: 1.2 }}>구매자</span>
                            <span style={{ ...styles.col, flex: 2 }}>상품</span>
                            <span style={{ ...styles.col, flex: 1, textAlign: 'right' }}>금액</span>
                            <span style={{ ...styles.col, flex: 0.7, textAlign: 'center' }}>상태</span>
                            <span style={{ ...styles.col, flex: 0.6, textAlign: 'center' }}>날짜</span>
                        </div>

                        {/* 테이블 바디 */}
                        {orders.map((order) => (
                            <div key={order.id} style={styles.tableRow}>
                                <span style={{ ...styles.cell, flex: 1.2, fontWeight: 600, color: '#1e293b' }}>
                                    {order.buyerName}
                                </span>
                                <span style={{ ...styles.cell, flex: 2, color: '#475569' }}>
                                    {order.productName}
                                </span>
                                <span style={{ ...styles.cell, flex: 1, textAlign: 'right', fontWeight: 700, color: '#1e293b' }}>
                                    {order.total.toLocaleString()}원
                                </span>
                                <span style={{ ...styles.cell, flex: 0.7, textAlign: 'center' }}>
                                    <span style={{
                                        padding: '3px 8px',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        backgroundColor: order.status === 'delivered' ? '#dcfce7' : '#e0e7ff',
                                        color: order.status === 'delivered' ? '#16a34a' : '#4338ca',
                                    }}>
                                        {statusLabel(order.status)}
                                    </span>
                                </span>
                                <span style={{ ...styles.cell, flex: 0.6, textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                                    {formatDate(order.createdAt)}
                                </span>
                            </div>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: '32px',
        maxWidth: '1000px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '28px',
    },
    title: {
        fontSize: '26px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    monthSelector: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginBottom: '24px',
    },
    monthBtn: {
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        backgroundColor: '#fff',
        color: '#475569',
        cursor: 'pointer',
    },
    monthText: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '18px',
        fontWeight: '700',
        color: '#1e293b',
    },
    summary: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '32px',
        padding: '24px',
        backgroundColor: '#fff',
        borderRadius: '16px',
        border: '2px solid #e2e8f0',
        marginBottom: '24px',
    },
    summaryItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    summaryLabel: {
        fontSize: '13px',
        color: '#94a3b8',
        fontWeight: '500',
    },
    summaryValue: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#1e293b',
    },
    divider: {
        width: '1px',
        height: '40px',
        backgroundColor: '#e2e8f0',
    },
    listCard: {
        backgroundColor: '#fff',
        borderRadius: '16px',
        border: '2px solid #e2e8f0',
        overflow: 'hidden',
    },
    tableHeader: {
        display: 'flex',
        padding: '14px 24px',
        backgroundColor: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
    },
    col: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#94a3b8',
    },
    tableRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '16px 24px',
        borderBottom: '1px solid #f1f5f9',
        transition: 'background 0.15s',
    },
    cell: {
        fontSize: '14px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingRight: '8px',
    },
    empty: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        gap: '12px',
    },
    emptyText: {
        fontSize: '15px',
        color: '#94a3b8',
        fontWeight: '500',
        margin: 0,
    },
};

export default SellerSettlement;
